import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './firebase';
import { ThemeProvider } from './context/ThemeContext';
import { Analytics } from "@vercel/analytics/react";

// ၁။ React Lazy ကိုအသုံးပြု၍ Page များကို လိုအပ်မှသာ (Dynamic) Import လုပ်ခြင်း
const HomePage = lazy(() => import('./HomePage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const KanjiListPage = lazy(() => import('./pages/KanjiListPage'));
const FavoritesPage = lazy(() => import('./pages/FavouritesPage'));
const QuizSelectionPage = lazy(() => import('./pages/QuizSelectionPage'));
const JlptQuizPage = lazy(() => import('./pages/JlptQuizPage'));
const QuizResultPage = lazy(() => import('./pages/QuizResultPage'));
const EditProfilePage = lazy(() => import('./pages/EditProfilePage'));
const AiExplanationPage = lazy(() => import('./pages/AiExplanationPage'));
const FlashcardPage = lazy(() => import('./pages/FlashcardPage'));

// ၄။ Loading အစားထိုးပြသမည့် Fallback UI (Suspense နှင့် Protected Route တွင်သုံးရန်)
const PageLoader = () => (
  <div className="min-h-screen bg-slate-50 dark:bg-gray-900 flex items-center justify-center transition-colors duration-300">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-400"></div>
  </div>
);

// ၃။ Login ဝင်ထားမှသာ ကြည့်ခွင့်ပေးမည့် Protected Route Component
const ProtectedRoute = ({ user, loading, children }) => {
  if (loading) {
    // Auth စစ်ဆေးနေချိန်တွင် အဝိုင်းလေးသာပြထားမည်
    return <PageLoader />;
  }
  if (!user) {
    // Login ဝင်မထားပါက Login Page သို့ ပြန်ပို့မည်
    return <Navigate to="/login" replace />;
  }
  // Login ဝင်ထားပါက သက်ဆိုင်ရာ Page ကို ပြမည်
  return children;
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Firebase Auth စစ်ဆေးခြင်းကို နောက်ကွယ်တွင် လုပ်ဆောင်မည်
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
      } else {
        setUser(null);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // ၂။ ယခင်ကဲ့သို့ Website တစ်ခုလုံးကို Block လုပ်ထားသော Global Loading ကို ဖြုတ်လိုက်ပါပြီ။
  // if (loading) { return <Loading /> } <--- ဖြုတ်လိုက်ပါသည်။

  return (
    <ThemeProvider>
      <Router>
        <div className="min-h-screen bg-slate-50 dark:bg-gray-900 transition-colors duration-300">
          
          {/* Lazy load လုပ်ထားသော Page များ အဆင်သင့်မဖြစ်ခင် PageLoader ကို ပြထားမည် */}
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* --- PUBLIC ROUTES (Login မလိုဘဲ ကြည့်နိုင်သော Page များ) --- */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/" element={<HomePage />} />
              
              <Route path="/list/:level" element={<KanjiListPage />} />
              <Route path="/list/:level/:param1" element={<KanjiListPage />} />
              <Route path="/list/:level/:book/:param2" element={<KanjiListPage />} />
              <Route path="/flashcards" element={<FlashcardPage />} />

              {/* --- QUIZ ROUTES (ယခုအခါ Public အဖြစ် ပြောင်းလဲထားပါသည်) --- */}
              <Route path="/quiz-select/:level" element={<QuizSelectionPage />} />
              <Route path="/quiz/:level/:examId" element={<JlptQuizPage />} />
              <Route path="/quiz-result" element={<QuizResultPage />} />

              {/* --- PROTECTED ROUTES (Login ဝင်ထားမှ ကြည့်နိုင်သော Page များ) --- */}
              <Route 
                path="/favorites" 
                element={
                  <ProtectedRoute user={user} loading={loading}>
                    <FavoritesPage />
                  </ProtectedRoute>
                } 
              />
              
              <Route 
                path="/edit-profile" 
                element={
                  <ProtectedRoute user={user} loading={loading}>
                    <EditProfilePage />
                  </ProtectedRoute>
                } 
              />
              
              <Route 
                path="/ai-explanation" 
                element={
                  <ProtectedRoute user={user} loading={loading}>
                    <AiExplanationPage />
                  </ProtectedRoute>
                } 
              />

              {/* Fallback: Redirect unknown URLs to Home */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </div>
        
        <Analytics />
      </Router>
    </ThemeProvider>
  );
}

export default App;